import { useEffect, useState } from "react";

import {
  DEFAULT_PAGE_SIZE,
  pageSizeFromAspect,
  readHtmlPageSize,
} from "../../common/HtmlDesigner/model/pageSize";

const sizeCache = new Map();
const RESOLVE_TIMEOUT_MS = 6000;

function readFromDocument(url, signal) {
  if (typeof fetch !== "function") return Promise.resolve(null);
  return fetch(url, { signal, credentials: "omit" })
    .then((response) => (response.ok ? response.text() : ""))
    .then(readHtmlPageSize)
    .catch(() => null);
}

function readFromThumbnail(thumbnailUrl) {
  if (!thumbnailUrl || typeof Image === "undefined") {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () =>
      resolve(pageSizeFromAspect(image.naturalWidth, image.naturalHeight));
    image.onerror = () => resolve(null);
    image.src = thumbnailUrl;
  });
}

const initialState = (url) => ({
  size: sizeCache.get(url) || DEFAULT_PAGE_SIZE,
  ready: !url || sizeCache.has(url),
});

/**
 * Page size of an issued HTML certificate. Read from the document when its
 * host allows cross-origin reads, otherwise from the shape of its thumbnail
 * (rendered from the same page). Falls back to A4 landscape.
 *
 * `ready` turns true once the size is known or every source has failed, so
 * callers can hold the frame back instead of showing it at the wrong shape.
 */
export function useHtmlPageSize(url, thumbnailUrl) {
  const [state, setState] = useState(() => initialState(url));

  useEffect(() => {
    if (!url || sizeCache.has(url)) {
      setState(initialState(url));
      return undefined;
    }
    setState({ size: DEFAULT_PAGE_SIZE, ready: false });
    let active = true;
    let timer = null;
    const controller = new AbortController();
    const finish = (size) => {
      if (!active) return;
      active = false;
      clearTimeout(timer);
      if (size) sizeCache.set(url, size);
      setState({ size: size || DEFAULT_PAGE_SIZE, ready: true });
    };
    timer = setTimeout(() => finish(null), RESOLVE_TIMEOUT_MS);
    const fromThumbnail = readFromThumbnail(thumbnailUrl);
    readFromDocument(url, controller.signal)
      .then((exact) => exact || fromThumbnail)
      .then(finish);
    return () => {
      active = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [url, thumbnailUrl]);

  return state;
}
